/**
 * src/entities/room/data/starterTemplates.ts
 *
 * Static bundled starter templates for the "aha moment" room-creation flow.
 * Mirrors the SURFACE_RECIPES pattern from src/entities/recipe/data/initialData.ts.
 *
 * Rules:
 * - Pure data only; no functions, no React, no cross-entity runtime imports.
 * - dims/openings in METRES — the single unit contract of Room.dims / Opening
 *   used by geometry.ts, RoomInput.tsx (which divides cm input by 100) and
 *   OpeningsManager.tsx. createRoom stores dims verbatim without conversion,
 *   so templates MUST already be in metres (e.g. 2.30, not 230).
 * - recipeIds are string IDs only — no runtime import of entities/recipe (FSD boundary).
 * - Every recipeId here is verified to exist in initialData.ts SURFACE_RECIPES.
 */

import type { Opening } from '../model/types';

export interface StarterTemplate {
  id: string;
  nameKey: string;
  /** Russian fallback name — displayed when i18n resolves to nothing */
  name: string;
  dims: {
    length: number;
    width: number;
    height: number;
  };
  openings?: Opening[];
  /** IDs from SURFACE_RECIPES — verified against initialData.ts before commit */
  recipeIds: string[];
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: 'tpl_bathroom',
    nameKey: 'templates.bathroom.name',
    name: 'Ванная',
    dims: { length: 2.30, width: 1.70, height: 2.50 },
    openings: [{ type: 'door', width: 0.70, height: 2.00, hasSlopes: false }],
    recipeIds: [
      'recipe_wall_tile_bathroom',
      'recipe_floor_tile_bathroom',
      'recipe_plumbing_bathroom_full',
      'recipe_elec_bathroom',
      'recipe_general_openings',
    ],
  },
  {
    id: 'tpl_kitchen',
    nameKey: 'templates.kitchen.name',
    name: 'Кухня',
    dims: { length: 4.00, width: 3.00, height: 2.70 },
    openings: [{ type: 'door', width: 0.80, height: 2.00, hasSlopes: false }],
    recipeIds: [
      'recipe_wall_putty_paint',
      'recipe_tile_kitchen_apron',
      'recipe_tile_floor_kitchen',
      'recipe_plumbing_kitchen',
      'recipe_elec_kitchen',
      'recipe_ceil_paint',
    ],
  },
  {
    id: 'tpl_bedroom',
    nameKey: 'templates.bedroom.name',
    name: 'Спальня',
    dims: { length: 4.00, width: 4.00, height: 2.70 },
    openings: [{ type: 'door', width: 0.80, height: 2.00, hasSlopes: false }],
    recipeIds: [
      'recipe_wall_putty_paint',
      'recipe_floor_laminate',
      'recipe_ceil_paint',
      'recipe_elec_room',
    ],
  },
  {
    id: 'tpl_hallway',
    nameKey: 'templates.hallway.name',
    name: 'Коридор',
    dims: { length: 3.00, width: 1.20, height: 2.70 },
    openings: [{ type: 'door', width: 0.80, height: 2.00, hasSlopes: false }],
    recipeIds: [
      'recipe_wall_putty_paint',
      'recipe_floor_tile',
      'recipe_ceil_paint',
    ],
  },
];
